import { Button } from '@/dc-shared-web/src'
import { Form } from '@dc-shared-web/components/shadcn/form'
import { UseFormReturn } from 'react-hook-form'
import { Portfolio, Project, Contract } from '@/integrations/trimble/types'
import ExportOptions from '@/app/core/components/ProjectSight/ExportOptions'
import DestinationFields from '@/app/core/components/ProjectSight/DestinationFields'

interface ChecklistOption {
  label: string
  fieldName: string
  className: string
  show: boolean
  id: string
  optionGroup: 1 | 2 | 3
  questionOptions: {
    label: string
    fieldName: string
  }[]
  additionalOptions: {
    label: string
    fieldName: string
    show?: boolean
  }[]
}

interface PlaybookOption {
  label: string
  fieldName: string
  className: string
  show: boolean
  id: string
  additionalOptions: never[]
}

interface FormStateProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>
  documentName?: string
  checklistOptions?: ChecklistOption[]
  playbookOptions?: PlaybookOption[]
  portfolios: Portfolio[]
  projects: Project[]
  contracts: Contract[]
  loadingPortfolios: boolean
  loadingProjects: boolean
  loadingContracts: boolean
  watchPortfolio: string
  watchProject: string
  isExportDisabled: boolean
  onCancel: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (values: any) => void
}

/**
 * Form state component for ProjectSight modal
 * Combines export options and destination fields
 */
export const FormState: React.FC<FormStateProps> = ({
  form,
  documentName,
  checklistOptions,
  playbookOptions,
  portfolios,
  projects,
  contracts,
  loadingPortfolios,
  loadingProjects,
  loadingContracts,
  watchPortfolio,
  watchProject,
  isExportDisabled,
  onCancel,
  onSubmit,
}) => {
  // Get document text for display
  const getDocumentText = () => {
    if (documentName) {
      return `Export "${documentName}" to your ProjectSight Trimble account.`
    }
    return `Export to your ProjectSight Trimble account.`
  }

  return (
    <>
      <div
        className='tw-text-[14px] tw-text-[#6E6E73] tw-mt-2'
        id='projectsight-dialog-description'
      >
        {getDocumentText()} Please select the destination portfolio, project,
        and contract.
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='tw-space-y-6'>
          <ExportOptions
            form={form}
            checklistOptions={checklistOptions}
            playbookOptions={playbookOptions}
          />

          <DestinationFields
            form={form}
            portfolios={portfolios}
            projects={projects}
            contracts={contracts}
            loadingPortfolios={loadingPortfolios}
            loadingProjects={loadingProjects}
            loadingContracts={loadingContracts}
            watchPortfolio={watchPortfolio}
            watchProject={watchProject}
          />

          <div className='tw-flex tw-justify-end tw-gap-2 tw-mt-3'>
            <Button
              type='button'
              variant='outline'
              onClick={onCancel}
              aria-label='Cancel export'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={isExportDisabled}
              aria-label='Export to ProjectSight'
            >
              Export
            </Button>
          </div>
        </form>
      </Form>
    </>
  )
}

export default FormState
